<template>
    <div>

        <div v-if="loading" class="text-center">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>
        <div v-else>
            <vue-headful :title="'Edit Equipment Type :: MeritEHS'"/>

            <h4 class="py-3 mb-4">
                <ol class="breadcrumb font-weight-bold m-0">
                    <li class="breadcrumb-item font-weight-light">
                        <router-link to="/equipment">
                            <i class="fa fa-truck"></i> Equipment
                        </router-link>
                    </li>
                    <li class="breadcrumb-item font-weight-light">
                        <router-link to="/equipment#types">
                            Types
                        </router-link>
                    </li>
                    <template v-if="equipment_type_id">
                        <li class="breadcrumb-item font-weight-light">
                            <router-link :to="`/equipment/types/view/${equipment_type_id}`">
                                {{ equipment_type.equipment_type_name }}
                            </router-link>
                        </li>
                        <li class="breadcrumb-item active"><i class="fa fa-edit"></i> Edit</li>
                    </template>
                    <template v-else>
                        <li class="breadcrumb-item active"><i class="fa fa-plus"></i> Create</li>
                    </template>
                </ol>
            </h4>

            <ul class="search-nav nav nav-tabs tabs-alt container-p-x container-m--x mb-4">
                <b-nav-item :active="curTab === 'general'" @click="curTab = 'general'">
                    General Settings
                </b-nav-item>
                <b-nav-item :active="curTab === 'alerts'" @click="curTab = 'alerts'" v-if="equipment_type_id">
                    Date Alerts ({{ rules.length }})
                </b-nav-item>
                <b-nav-item :active="curTab === 'custom'" @click="curTab = 'custom'" v-if="equipment_type_id">
                    Custom Fields ({{ fields.length }})
                </b-nav-item>
            </ul>

            <div v-if="curTab === 'general'">
                <button type="button" class="btn btn-sm btn-success mb-2" @click="saveType" :disabled="saving">
                    <i class="fa fa-floppy-o"></i> Save Equipment Type
                </button>

                <div class="row">
                    <div class="col-xl-8 col-12">
                        <div class="card">
                            <div class="card-body">
                                <div v-bind:class="{ 'form-group': true, 'is-invalid': errors.equipment_type_name }" class="row">
                                    <label class="col-sm-4 col-form-label text-sm-right">Equipment Type:</label>
                                    <div class="col-sm-8">
                                        <input type="text" v-model="equipment_type.equipment_type_name" :class="{ 'form-control': true, 'is-invalid': errors.equipment_type_name }" placeholder="Enter Equipment Type Name" />
                                        <span v-if="errors.equipment_type_name" class="text-danger">
                                            <span v-for="error in errors.equipment_type_name">{{ error }}</span>
                                        </span>
                                    </div>
                                </div>

                                <div v-bind:class="{ 'form-group': true, 'is-invalid': errors.equipment_usage_type_id }" class="row">
                                    <label class="col-sm-4 col-form-label text-sm-right">Default Equipment Usage Type:</label>
                                    <div class="col-sm-8">
                                        <select name="equipment_usage_type_id" v-model="equipment_type.equipment_usage_type_id" class="form-control">
                                            <option :value="null">-- Select a Usage Type --</option>
                                            <option v-for="(type, value) in equipment_usage_types" :value="value">{{type}}</option>
                                        </select>
                                        <span v-if="errors.equipment_usage_type_id" class="text-danger">
                                            <span v-for="error in errors.equipment_usage_type_id">{{ error }}</span>
                                        </span>
                                    </div>
                                </div>

                                <div v-bind:class="{ 'form-group': true, 'is-invalid': errors.equipment_usage_type_id }" class="row">
                                    <label class="col-sm-4 col-form-label text-sm-right">Calibration Valid For (Days):</label>
                                    <div class="col-sm-8">
                                        <b-form-input type="number" v-model="equipment_type.days_calibration_valid_for" placeholder="Enter Number of Days that the Calibration is Valid For" />

                                        <div class="text-info">
                                            If set, MeritEHS will highlight calibration dates that are past the age of the setting above.
                                        </div>

                                        <span v-if="errors.days_calibration_valid_for" class="text-danger">
                                            <span v-for="error in errors.days_calibration_valid_for">{{ error }}</span>
                                        </span>
                                    </div>
                                </div>

                                <div v-bind:class="{ 'form-group': true}" class="row">
                                    <label class="col-sm-4 col-form-label text-sm-right">Require Inspection on Check In:</label>
                                    <div class="col-sm-8">
                                        <b-form-checkbox v-model="equipment_type.require_inspection" :value="1" :unchecked-value="0" plain />
                                    </div>
                                </div>

                                <div v-bind:class="{ 'form-group': true}" class="row">
                                    <label class="col-sm-4 col-form-label text-sm-right">Require Inspection on Issuing / Check Out:</label>
                                    <div class="col-sm-8">
                                        <b-form-checkbox v-model="equipment_type.require_insp_on_checkout" :value="1" :unchecked-value="0" plain />
                                    </div>
                                </div>

                                <div v-bind:class="{ 'form-group': true}" class="row">
                                    <label class="col-sm-4 col-form-label text-sm-right">Prevent Check Out w/ Open Corrective Action(s):</label>
                                    <div class="col-sm-8">
                                        <b-form-checkbox v-model="equipment_type.restrict_checkout_w_open_ca" :value="1" :unchecked-value="0" plain />
                                    </div>
                                </div>

                                <div v-bind:class="{ 'form-group': true}" class="row">
                                    <label class="col-sm-4 col-form-label text-sm-right">Require Jobsite When Issuing:</label>
                                    <div class="col-sm-8">
                                        <b-form-checkbox v-model="equipment_type.require_tracking_jobsite" :value="1" :unchecked-value="0" plain />
                                    </div>
                                </div>

                                <div v-bind:class="{ 'form-group': true}" class="row">
                                    <div class="col-sm-4 text-sm-right">
                                        <label class="col-form-label">Use current value instead of difference when inputting usage:</label>
                                        <small>For example, instead of saying you added 10,000 miles to a vehicle, you'd input 60,000 (the current odometer reading)</small>
                                    </div>
                                    <div class="col-sm-8">
                                        <b-form-checkbox v-model="equipment_type.use_absolute_usage" :value="1" :unchecked-value="0" plain />
                                    </div>
                                </div>

                                <div v-bind:class="{ 'form-group': true}" class="row">
                                    <label class="col-sm-4 col-form-label text-sm-right">Is Active:</label>
                                    <div class="col-sm-8">
                                        <b-form-checkbox v-model="equipment_type.is_active" :value="1" :unchecked-value="0" plain />
                                    </div>
                                </div>

                                <hr>

                                <file-uploader assetType="EquipmentType" :assetId="parseInt(equipment_type_id)" />
                                <br>

                                <file-list :files="files" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="curTab === 'alerts'">
                <div class="card">
                    <div class="card-header">
                        Date Alert Rules <router-link :to="`/admin/equipment-alerts/create?equipment_type_id=${equipment_type_id}`" class="btn btn-sm btn-success"><i class="fa fa-plus"></i> New</router-link>
                    </div>
                    <div class="card-body">
                        <v-client-table ref="emailRulesTable" :columns="columns" :options="options" :data="rules">

                            <template slot="id" slot-scope="props">
                                <router-link  :to="`/admin/equipment-alerts/edit/${props.row.id}`" title="Edit Date Alert" class="btn btn-sm btn-secondary">
                                    <i class="fa fa-edit"></i> Edit
                                </router-link>
                                <router-link :to="`/admin/equipment-alerts/delete/${props.row.id}`" title="Delete Date Alert" class="btn btn-sm btn-danger">
                                <i class="fa fa-trash"></i> Delete
                            </router-link>
                            </template>

                            <router-link slot="alert_name" slot-scope="props" :to="`/admin/equipment-alerts/view/${props.row.id}`" title="View Date Alert">
                                {{ props.row.alert_name }}
                            </router-link>

                            <template slot="is_active" slot-scope="props">
                                <label v-if="props.row.is_active == 1" class="badge badge-success">Active</label>
                                <label v-if="props.row.is_active == 0" class="badge badge-warning">Inactive</label>
                            </template>

                            <!-- <template slot="child_row" slot-scope="props">
                                <div class="row" style="margin-right: -0.625rem; margin-left: -0.625rem;">
                                    <div class="col-md-4 col-12 mb-md-0 mb-3">
                                        <span class="font-weight-bold">Description</span><br>
                                        <div v-html="props.row.description"></div>
                                    </div>
                                    <div class="col-md-4 col-12 mb-md-0 mb-3">
                                        <span class="font-weight-bold">Conditions</span><br>
                                        WHEN:
                                        <ol>
                                            <li v-for="(cond, key) in props.row.conditions">
                                                <template v-if="key > 0">AND:</template>
                                                {{ cond.field_label }} {{ cond.logic }} {{ cond.cond_label }}
                                            </li>
                                        </ol>
                                    </div>
                                    <div class="col-md-4 col-12 mb-md-0 mb-3">
                                        <span class="font-weight-bold">Recipients</span><br>
                                        Send Email To:
                                        <ol>
                                            <li v-for="(recip, key) in props.row.recipients">
                                                {{ recip.target_type }} -
                                                <template v-if="recip.target_type == 'Employee' || recip.target_type == 'User'">
                                                    {{ recip.target_label.FirstName }} {{ recip.target_label.LastName }}
                                                </template>
                                                <template v-else>{{ recip.target_label }}</template>
                                            </li>
                                        </ol>
                                    </div>
                                </div>
                            </template> -->
                        </v-client-table>
                    </div>
                </div>
            </div>

            <div v-if="curTab === 'custom'">
                <div class="mt-2 mb-4">
                    <button type="button" class="btn btn-sm btn-success" @click="saveType()">
                        <i class="fa fa-floppy-o"></i> Save Equipment Type
                    </button>
                    <button type="button" class="btn btn-sm btn-warning" @click="show_preview = true">
                        <i class="fa fa-eye"></i> Preview Custom Fields
                    </button>
                </div>

                <div class="row">
                    <div class="col-lg-9 col-12">
                        <div class="card mb-3">
                            <div class="card-header">
                                <div class="row">
                                    <div class="col-md-6 col-12">
                                        Custom Fields
                                        <button class="btn btn-sm btn-light" @click="show_fields = true" title="Add Field">
                                            <i class="fa fa-plus-circle text-success"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div class="card-body bg-light">
                                <div v-if="fields.length == 0" style="border: 1px dashed #ccc; padding: 10px 20px; text-align: center; height: 50px; line-height: 50px; background: #fff;">
                                    <p style="display: inline-block; vertical-align: middle; line-height: normal; color: #ccc;">Click the <i class="fa fa-plus-circle text-success"></i> in the Heading to start adding fields</p>
                                </div>
                                <div>
                                    <div class="card mb-2" v-for="(field, elemKey) in fields" :key="field.id">
                                        <div :class="{'card-header': true, 'text-danger': errors[field.id]}">
                                            <div class="row">
                                                <div class="col-md-6 col-12 form-inline">
                                                    <span class="mr-2">{{ field.label ? field.label : field.name }}: <span class="text-muted" title="This is the field's unique ID.">{{ field.id }}</span></span>
                                                </div>
                                                <div class="col-md-6 col-12">
                                                    <div class="float-right">
                                                        <!-- <button class="btn btn-sm btn-light" v-on:click="toggleField(field.id)" :title="(field.show_body ? 'Hide' : 'Show') + ' Field Options'">
                                                            <i :class="'fa fa-edit'"></i>
                                                        </button> -->
                                                        <button class="btn btn-sm btn-light" v-on:click="fields.splice(elemKey, 1)" title="Delete Field" v-if="!equipment_type.published_at || createdAfterPublished(field)">
                                                            <i class="fa fa-trash text-danger"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="card-body">
                                            <div class="form-group row mb-2" v-if="errors[field.id]">
                                                <div class="col-sm-10 offset-sm-1">
                                                    <div class="alert alert-danger">
                                                        <strong>Validation Errors:</strong>
                                                        <ul>
                                                            <li v-for="err in errors[field.id]">{{ err }}</li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="form-group row">
                                                <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">Only Render This field If:</label>
                                                <div class="col-sm-8 pt-2">
                                                    <b-form-select v-model="field.parent_field_id" :options="conditionalFieldOptions(field.id)" size="sm">
                                                        <template v-slot:first>
                                                            <option :value="null">-- Select A Parent Field. Leave Blank to Always Show this Field --</option>
                                                        </template>
                                                    </b-form-select>
                                                    <b-form-select v-if="field.parent_field_id" v-model="field.parent_field_value" :options="conditionalFieldValues(field.parent_field_id)" size="sm" class="mt-2">
                                                        <template v-slot:first>
                                                            <option :value="null">-- Select A Value --</option>
                                                        </template>
                                                    </b-form-select>
                                                </div>
                                            </div>

                                            <template v-if="field.name != 'Paragraph' && field.name != 'Heading'">
                                                <div class="form-group row">
                                                    <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">Required:</label>
                                                    <div class="col-sm-8 pt-2">
                                                        <b-form-checkbox v-model="field.is_required" :value="true" :unchecked-value="false" plain></b-form-checkbox>
                                                    </div>
                                                </div>
                                            </template>

                                            <div class="form-group row" v-if="field.name != 'Paragraph'">
                                                <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">Label:</label>
                                                <div class="col-sm-8">
                                                    <b-form-input v-model="field.label" placeholder="Enter a Field Label" size="sm" class="mr-2" />
                                                </div>
                                            </div>

                                            <div class="form-group row">
                                                <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">Help Text:</label>
                                                <div class="col-sm-8">
                                                    <b-form-input v-model="field.help_text" placeholder="Enter Some Help Text for the Field" size="sm" />
                                                </div>
                                            </div>

                                            <div class="form-group row" v-if="('placeholder' in field)">
                                                <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">Placeholder:</label>
                                                <div class="col-sm-8">
                                                    <b-form-input v-model="field.placeholder" placeholder="Enter a Placeholder Label" size="sm" />
                                                </div>
                                            </div>

                                            <template v-if="field.name == 'Date'">
                                                <div class="form-group row">
                                                    <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">
                                                        <i class="fa fa-question-circle" title="If enabled, this field can be used for Equipment Date Alerts and will be pulled into the Equipment Dates section on the main equipment list."></i>
                                                    </label>
                                                    <div class="col-sm-8 pt-2">
                                                        <b-form-checkbox v-model="field.use_for_alerts" :value="true" :unchecked-value="false" plain>Allow for use in Equipment Alerts</b-form-checkbox>
                                                    </div>
                                                </div>
                                                <div class="form-group row">
                                                    <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">
                                                        Past or Future Date: <i class="fa fa-question-circle" title="A past date would be like the last time the oil was changed in a vehicle. A future date would be the next time an oil change is needed. This value should be consistent with what you plan to capture, plus validation will follow this logic"></i>
                                                    </label>
                                                    <div class="col-sm-8 pt-2">
                                                        <b-form-select v-model="field.past_or_future" size="sm" :options="[{value: null, text: '-- Select Past or Future --'}, {value: 'past', text: 'Past'}, {value: 'future', text: 'Future'}]"></b-form-select>
                                                    </div>
                                                </div>
                                            </template>

                                            <template v-if="field.name == 'Dropdown'">
                                                <div class="form-group row">
                                                    <label class="col-lg-3 col-sm-4 col-form-label text-sm-right"></label>
                                                    <div class="col-sm-8 pt-2">
                                                        <b-form-checkbox v-model="field.allow_multiple" :value="true" :unchecked-value="false" plain>Allow Multiple Selections</b-form-checkbox>
                                                    </div>
                                                </div>
                                                <div class="form-group row">
                                                    <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">
                                                        Options:<br>
                                                        <a href="javascript:void(0)" v-on:click="field.options.push('')">Add Option <i class="fa fa-plus-circle"></i></a>
                                                    </label>
                                                    <div class="col-sm-8">
                                                        <div v-for="(option, key) in field.options" class="form-inline mb-2">
                                                            <b-form-input v-model="field.options[key]" placeholder="Enter Option" size="sm" />

                                                            <template v-if="!equipment_type.published_at || createdAfterPublished(field)">
                                                                <a href="javascript:void(0)" class="text-danger ml-2" v-on:click="field.options.splice(key, 1)"><i class="fa fa-trash"></i></a>
                                                            </template>
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>

                                            <template v-if="field.name == 'Radio' || field.name == 'Checkbox'">
                                                <div class="form-group row">
                                                    <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">Vertical Display:</label>
                                                    <div class="col-sm-8 pt-2">
                                                        <b-form-checkbox v-model="field.display_vertical" :value="true" :unchecked-value="false" plain>Display the options vertically. Default is inline.</b-form-checkbox>
                                                    </div>
                                                </div>
                                                <!-- <div class="form-group row">
                                                    <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">Enable "Other":</label>
                                                    <div class="col-sm-8 pt-2">
                                                        <b-form-checkbox v-model="field.enable_other" :value="true" :unchecked-value="false" plain>Let users to enter an unlisted option</b-form-checkbox>
                                                    </div>
                                                </div> -->
                                                <div class="form-group row">
                                                    <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">
                                                        Options:<br>
                                                        <a href="javascript:void(0)" v-on:click="field.options.push('')">Add Option <i class="fa fa-plus-circle"></i></a>
                                                    </label>
                                                    <div class="col-sm-8">
                                                        <div v-for="(option, key) in field.options" class="form-inline">
                                                            <!-- <span class="mr-2">Option {{ key + 1 }}:</span> -->
                                                            <b-form-input v-model="field.options[key]" placeholder="Enter Option" size="sm" />
                                                            <button type="button" class="btn btn-link text-danger" v-on:click="field.options.splice(key, 1)"><i class="fa fa-trash"></i></button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>

                                            <div class="form-group row" v-if="field.name == 'Textarea'">
                                                <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">Rich Text:</label>
                                                <div class="col-sm-8">
                                                    <b-form-checkbox v-model="field.enable_rich_text" :value="true" :unchecked-value="false" plain>
                                                        Enable Rich Text
                                                    </b-form-checkbox>
                                                    <p>This will activate the rich text editor that allows users to bold, highlight, list, and edit text.</p>
                                                </div>
                                            </div>

                                            <template v-if="field.name == 'Number'">
                                                <div class="form-group row"">
                                                    <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">Min:</label>
                                                    <div class="col-sm-8">
                                                        <b-form-input v-model="field.min" placeholder="Enter Min Value" size="sm"></b-form-input>
                                                    </div>
                                                </div>
                                                <div class="form-group row"">
                                                    <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">Max:</label>
                                                    <div class="col-sm-8">
                                                        <b-form-input v-model="field.max" placeholder="Enter Max Value" size="sm"></b-form-input>
                                                    </div>
                                                </div>
                                                <div class="form-group row"">
                                                    <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">Step:</label>
                                                    <div class="col-sm-8">
                                                        <b-form-input v-model="field.step" placeholder="Enter Step Value" size="sm"></b-form-input>
                                                    </div>
                                                </div>
                                            </template>

                                            <template v-if="field.name == 'Paragraph'">
                                                <div class="form-group row"">
                                                    <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">Block Text:</label>
                                                    <div class="col-sm-8">
                                                        <b-form-textarea v-model="field.text" placeholder="Enter Block Text" rows="5"></b-form-textarea>
                                                    </div>
                                                </div>
                                                <div class="form-group row"">
                                                    <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">Background Color:</label>
                                                    <div class="col-sm-8">
                                                        <b-form-radio-group plain v-model="field.color">
                                                            <b-form-radio :value="null">
                                                                <i class="fa fa-square-o"></i> -- None --
                                                            </b-form-radio><br/>
                                                            <b-form-radio value="primary">
                                                                <i class="fa fa-square text-primary"></i> Primary
                                                            </b-form-radio><br/>
                                                            <b-form-radio value="secondary">
                                                                <i class="fa fa-square text-secondary"></i> Secondary
                                                            </b-form-radio><br/>
                                                            <b-form-radio value="info">
                                                                <i class="fa fa-square text-info"></i> Info
                                                            </b-form-radio><br/>
                                                            <b-form-radio value="warning">
                                                                <i class="fa fa-square text-warning"></i> Warning
                                                            </b-form-radio><br/>
                                                            <b-form-radio value="success">
                                                                <i class="fa fa-square text-success"></i> Success
                                                            </b-form-radio><br/>
                                                            <b-form-radio value="danger">
                                                                <i class="fa fa-square text-danger"></i> Danger
                                                            </b-form-radio><br/>
                                                            <b-form-radio value="light">
                                                                <i class="fa fa-square text-light"></i> Light
                                                            </b-form-radio><br/>
                                                            <b-form-radio value="dark">
                                                                <i class="fa fa-square text-dark"></i> Dark
                                                            </b-form-radio><br/>
                                                        </b-form-radio-group>
                                                    </div>
                                                </div>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Modal Component -->
            <b-modal v-model="show_preview" title="Form Preview" size="lg" no-fade :no-close-on-backdrop="true" ok-only ok-variant="secondary" ok-title="Close">

                <equipment-sections-builder :fields="previewFields"></equipment-sections-builder>

            </b-modal>

            <b-modal v-model="show_fields" title="Add Field Preview" size="lg" no-fade :no-close-on-backdrop="true" ok-variant="success" ok-title="Add Field" @ok="fieldAdded">

                <b-form-radio-group plain v-model="new_field">
                    <strong>Standard Fields</strong>
                    <ul class="list-group mb-4 bg-white">
                        <li class="list-group-item" v-for="field in standardFields" :key="field.id">
                            <b-form-radio :value="field.id">{{ field.name }}</b-form-radio>
                        </li>
                    </ul>

                    <strong>Formatted Fields</strong>
                    <ul class="list-group mb-4 bg-white">
                        <li class="list-group-item" v-for="field in formattedFields" :key="field.id">
                          <b-form-radio :value="field.id">{{ field.name }}</b-form-radio>
                        </li>
                    </ul>

                    <strong>Helpers - Insert Help Messages or Reminders for Users filling out your form.</strong>
                    <ul class="list-group bg-white">
                        <li class="list-group-item" v-for="field in helperFields" :key="field.id">
                          <b-form-radio :value="field.id">{{ field.name }}</b-form-radio>
                        </li>
                    </ul>
                </b-form-radio-group>

            </b-modal>
        </div>
    </div>
</template>

<script>
    export default {
        data: function() {
            return {
                loading: false,
                saving: false,
                fields: [],
                show_fields: false,
                new_field: null,
                show_preview: false,
                standardFields: [
                    {id: 1, name: 'Text', placeholder: '', parent_field_id: null},
                    {id: 2, name: 'Dropdown', placeholder: '', parent_field_id: null, options: [], allow_multiple: false},
                    {id: 3, name: 'Radio', parent_field_id: null, options: [], display_vertical: false, enable_other: false},
                    {id: 4, name: 'Checkbox', parent_field_id: null, options: [], display_vertical: false, enable_other: false},
                    {id: 5, name: 'Textarea', parent_field_id: null, placeholder: '', enable_rich_text: false},
                ],
                formattedFields: [
                    {id: 6, name: 'Date', parent_field_id: null, placeholder: '', format: 'MM/DD/YYYY'},
                    {id: 7, name: 'Time', parent_field_id: null, placeholder: ''},
                    {id: 8, name: 'Email', parent_field_id: null, placeholder: ''},
                    {id: 9, name: 'Phone', parent_field_id: null, placeholder: '', format: 'XXX-XXX-XXXX'},
                    {id: 10, name: 'Number', parent_field_id: null, placeholder: '', min: null, max: null, step: null},
                ],
                helperFields: [
                    {id: 12, name: 'Heading', parent_field_id: null, tag: 'h1', text: ''},
                    {id: 13, name: 'Paragraph', parent_field_id: null, color: 'info', text: ''},
                ],
                equipment_type_id: this.$route.params.id,
                equipment_type: {
                    equipment_type_name: '',
                },
                columns: ['id', 'alert_name', 'is_active'],
                options: {
                    columnsDropdown: false,
                    headings: {
                        id: 'Actions',
                        alert_name: 'Name',
                        is_active: 'Status',
                    },
                    orderBy: {
                        ascending: false,
                        column: 'equip_alerts.id',
                    },
                },
                rules: [],
                curTab: 'general',
                equipment_usage_types: {},
                errors: {},
                files: [],
            }
        },

        computed: {
            previewFields(){
                //Setup
                let fields = [];

                //Loop through and build lists
                for(let i = 0; i < this.fields.length; i++)
                {
                    //Push onto options
                    fields.push(JSON.parse(JSON.stringify(this.fields[i])));
                }

                //Send it
                return fields;
            },
            conditionalFieldOptions_OLD(){
                //Build based on the list of sections and their fields
                let parentFields = [];

                //Loop through the sections
                for(let i = 0; i < this.fields.length; i++)
                {
                    //Check the type
                    if(this.fields[i].name != 'Dropdown' && this.fields[i].name != 'Radio' && this.fields[i].name != 'Checkbox')
                    {
                        continue;
                    }

                    //Add the Option
                    parentFields.push({value: this.fields[i].id, text: (this.fields[i].label ? this.fields[i].label : this.fields[i].id)});
                }

                //SEnd it
                return parentFields;
            },
        },

        watch: {
            curTab(newValue, oldValue) {
                if (newValue)
                {
                    if (newValue === 'general')
                    {
                        window.history.replaceState(null,null,`${this.$route.path}`)
                    }
                    else
                    {
                        window.history.replaceState(null,null,`${this.$route.path}#${this.curTab}`)
                    }
                }
            },
        },

        methods: {
            fieldAdded(bvEvt) {
                //Make sure the user selected a field
                if(!this.new_field)
                {
                    //Alert the user
                    this.$toastr.warning('Select a Field to Add');

                    //Keep the modal open
                    bvEvt.preventDefault();

                    //Stop here
                    return;
                }

                //Get the field by ID and send it
                var fieldDefinition = this.getFieldById(this.new_field);

                //Clone it
                this.fields.push(this.cloneField(fieldDefinition));

                //Reset
                this.new_field = null;
            },
            getFieldById(id){
                //Loop through all
                for(var i = 0; i < this.standardFields.length; i++)
                {
                    //Check the if
                    if(id == this.standardFields[i].id)
                    {
                        return this.standardFields[i];
                    }
                }

                //Loop through all
                for(var i = 0; i < this.formattedFields.length; i++)
                {
                    //Check the if
                    if(id == this.formattedFields[i].id)
                    {
                        return this.formattedFields[i];
                    }
                }

                //Loop through all
                for(var i = 0; i < this.helperFields.length; i++)
                {
                    //Check the if
                    if(id == this.helperFields[i].id)
                    {
                        return this.helperFields[i];
                    }
                }
            },
            //Clones a Field to a New Element
            cloneField(field){
                //Setup
                let newField = JSON.parse(JSON.stringify(field));

                //Modify the field
                newField.id = field.name.toLowerCase() + '-' + new Date().getTime();
                newField.show_body = true;

                //Send it
                return newField;
            },
            toggleField(fieldId){
                //Loop through all
                for(let i = 0; i < this.fields.length; i++)
                {
                    //Check the if
                    if(fieldId == this.fields[i].id)
                    {
                        //SEtter
                        if(this.fields[i].show_body == true)
                        {
                            this.fields[i].show_body = false;
                        }
                        else
                        {
                            this.fields[i].show_body = false;
                        }

                        //Set it
                        return;
                    }
                }
            },
            conditionalFieldOptions(fieldId){
                //Clone it
                let options = JSON.parse(JSON.stringify(this.conditionalFieldOptions_OLD));

                //Unset the ID from the Conditional Fields
                for(let i = 0; i < options.length; i++)
                {
                    //Check the ID
                    if(fieldId == options[i].value)
                    {
                        //Remove the Option
                        options.splice(i, 1);
                    }
                }

                //Send ti
                return options;
            },
            conditionalFieldValues(parentFieldId){
                //Setup
                let options = [];

                //Loop through the sections
                for(let i = 0; i < this.fields.length; i++)
                {
                    //Check the ID
                    if(parentFieldId == this.fields[i].id)
                    {
                        //Add All the Options
                        options = this.fields[i].options;
                    }
                }

                //Send it
                return options;
            },
            createdAfterPublished(item){
                //Check if the ID exists in the list
                if(this.publishedSections.length == 0)
                {
                    return true;
                }

                //Check for heading and paragraph types
                if(item.name && (item.name == 'Paragraph' || item.name == 'Heading'))
                {
                    //Stop here
                    return true;
                }

                //Check ALL
                for(var i = 0; i < this.publishedSections.length; i++)
                {
                    //Check each section
                    if(item.id == this.publishedSections.id)
                    {
                        return false;
                    }

                    //Check all the fields
                    for(var j = 0; j < this.publishedSections[i].fields.length; j++)
                    {
                        //Check the ID
                        if(item.id == this.publishedSections[i].fields[j].id)
                        {
                            //Match Found
                            return false;
                        }
                    }
                }

                //Return true - this was created after publish
                return true;
            },
            getErrorClass: function(attribute) {
                return {
                    'form-group has-error': this.errors[attribute],
                    'form-group': !this.errors[attribute],
                }
            },
            getEquipmentType: function() {
                //Loading
                this.loading = true;

                //Get the data
                this.$http
                    .get('admin/equipment-types-edit' + (this.equipment_type_id ? '/' + this.equipment_type_id : ''))
                    .then(response => {
                        //Set all the data
                        this.equipment_type = response.data.data;
                        this.files = response.data.files;
                        this.equipment_usage_types = response.data.equipment_usage_types;
                        this.fields = this.equipment_type.custom_fields ? JSON.parse(this.equipment_type.custom_fields) : [];
                        this.rules = response.data.rules;

                        //Set loading
                        this.loading = false;
                    })
                    .catch(response => {

                    });
            },

            saveType: function() {
                //Show loading
                this.saving = true;

                //Reset the errors
                this.errors = {};

                this.equipment_type.custom_fields = this.fields;
                this.equipment_type.custom_fields_picker = this.fields.filter((field) => {return !!field.use_for_alerts});

                //Send it
                this.$http
                .post('admin/equipment-types-edit', this.equipment_type)
                .then(response => {
                    //Alert the user?
                    this.$toastr.success('Equipment Type Saved');

                    //Return to main list?
                    this.$router.push('/equipment#types');
                })
                .catch(error => {
                    //Check for error
                    if(error.response.status === 422)
                    {
                        //Display validation errors
                        this.errors = error.response.data
                    }
                })
                .then(response => {
                    //Show loading
                    this.saving = false;
                });
            }
        },

        mounted() {
            this.getEquipmentType();

            if (this.$route.hash !== '' && this.$route.hash !== '#')
            {
                this.curTab = this.$route.hash.replace('#','')
            }
        }
    }
</script>
